/**
 * Pure mapping functions for converting an accepted offer into a project.
 * No DB or React deps so the mapping is unit-testable.
 *
 * VIB-496/VIB-556: The actual insert goes through the `create-project` edge
 * function; this file just shapes the payload.
 */
import type { OfferLine, Offer } from "@/hooks/use-offers";

export interface BudgetLinePayload {
  phase: string;
  description: string;
  quantity: number;
  unit: string;
  quantity_hours: number;
  amount: number;
  /** Estimert (ekstern) kostnad, se `offerLineEstimatedCost`. */
  estimated_cost: number;
  cost_type: string;
  /** Mal-kobling MÅ persisteres (CLAUDE.md), ellers kjenner ikke Timetildeling linja igjen. */
  template_id: string | null;
  /**
   * VIB-2866: rekkefølge innen fase, kopiert fra offer_lines.sort_order så
   * prosjektets budsjett viser linjene i samme rekkefølge som tilbudet.
   */
  sort_order: number | null;
  assignments: Array<{ userId: string; hours: number }>;
}

/**
 * Estimert kostnad på budsjettlinja ved konvertering.
 *
 * Eksterne linjer: innkjøpsprisen fra tilbudet (`purchase_price`), slik den
 * står. Ingen fallback til salgssummen (Erik, 23.10.2026): en tom innkjøpspris
 * er et tomt felt PL fyller ut på prosjektet, ikke et estimat.
 * Interne linjer har ingen ekstern kostnad; de dekkes av timer.
 *
 * Bakgrunn: konverteringen satte 0 på alle linjer (Sigurd, feedback
 * 23.10.2026, prosjekt 631), så est. kostnad og BF estimert ble feil.
 */
export function offerLineEstimatedCost(
  line: Pick<OfferLine, "cost_type" | "purchase_price">,
): number {
  if (line.cost_type !== "external") return 0;
  return Math.max(0, Number(line.purchase_price ?? 0));
}

/**
 * Maps offer_lines → budget_lines payload for the create-project edge function.
 *
 * Rules:
 * - `quantity_hours` = quantity only when unit is "t" (timer).
 *   "d" (dager) multipliseres med 8 (jf. VIB-555: 1d = 8t)
 * - `quantity` is always the raw value from the offer line
 * - Assignments are empty by default; the seller will assign employees later
 *   in the project (outside the scope of conversion)
 */
export function mapOfferLineToBudgetLine(line: OfferLine): BudgetLinePayload {
  const isHours = line.unit === "t";
  const isDays = line.unit === "d" && line.cost_type === "internal" && line.pricing_type === "hourly";
  const quantityHours = isHours
    ? line.quantity ?? 0
    : isDays
      ? (line.quantity ?? 0) * 8
      : 0;
  return {
    phase: line.phase,
    description: line.description,
    quantity: line.quantity ?? 0,
    unit: line.unit,
    quantity_hours: quantityHours,
    amount: line.amount ?? 0,
    estimated_cost: offerLineEstimatedCost(line),
    cost_type: line.cost_type,
    template_id: line.template_id ?? null,
    sort_order: line.sort_order ?? null,
    assignments: [],
  };
}

export interface OfferMetadataPayload {
  offer_total: number;
  overhead_pct: number;
  discount_pct: number;
  discount_amount: number;
  startup_invoice_pct: number;
  final_invoice_pct: number;
  include_travel_in_overhead: boolean;
}

export function buildOfferMetadata(offer: Offer): OfferMetadataPayload {
  return {
    offer_total: offer.total_amount,
    overhead_pct: offer.overhead_pct,
    discount_pct: offer.discount_pct,
    discount_amount: offer.discount_amount,
    startup_invoice_pct: offer.startup_invoice_pct,
    final_invoice_pct: offer.final_invoice_pct,
    include_travel_in_overhead: offer.include_travel_in_overhead === true,
  };
}

export interface ConvertOfferInput {
  offer: Offer;
  lines: OfferLine[];
  projectCode: string;
  projectName?: string;
  productionLeadId: string;
  advisorId?: string | null;
  estimatedDeadline?: string | null;
  owners: Array<{ user_id: string; ownership_pct: number }>;
  startupInvoicePct: number;
}

export interface CreateProjectPayload {
  project_code: string;
  name: string;
  customer_id: string | undefined;
  total_amount: number;
  startup_invoice_pct: number;
  is_internal: boolean;
  production_lead_id: string;
  advisor_id?: string;
  estimated_deadline?: string;
  owners: Array<{ user_id: string; ownership_pct: number }>;
  budget_lines: BudgetLinePayload[];
  offer_metadata: OfferMetadataPayload;
}

/**
 * Shapes the full create-project payload from an accepted offer.
 * Returns `null` if the offer is missing required customer info or the
 * owners list is empty — the UI should prevent this, but we guard here too.
 */
export function buildCreateProjectPayload(input: ConvertOfferInput): CreateProjectPayload | null {
  if (input.owners.length === 0) return null;
  if (!input.productionLeadId) return null;

  // Auto-genererte linjer (f.eks. tilbudsskriving) skal IKKE inn i
  // hovedprosjektets budsjett — de føres på internprosjekt etter
  // konvertering, så tilbudsskriver får credit der.
  const customerLines = input.lines.filter((l) => !l.is_auto_generated);

  const payload: CreateProjectPayload = {
    project_code: input.projectCode,
    name: input.projectName?.trim() || input.offer.title,
    customer_id: input.offer.customer_id ?? undefined,
    total_amount: input.offer.total_amount,
    startup_invoice_pct: input.startupInvoicePct,
    is_internal: false,
    production_lead_id: input.productionLeadId,
    owners: input.owners,
    budget_lines: customerLines.map(mapOfferLineToBudgetLine),
    offer_metadata: buildOfferMetadata(input.offer),
  };

  if (input.advisorId) payload.advisor_id = input.advisorId;
  if (input.estimatedDeadline) payload.estimated_deadline = input.estimatedDeadline;

  return payload;
}
